import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as React from 'react';
import { toast } from 'react-toastify';

import { listAccounts, updateAccount } from '@/pages/AccountSettings/api';
import type { PmsType, RentManagerConfig } from '@/Structures/types';
import type { Unit } from '@/Units/types';
import { updateUnitPmsConfig } from '@/Units/api';
import { naturalCompare } from '@/util';

import { SWLoadingButton } from '../../../components/LoadingButton';
import { SWLoadingButtonOutlined } from '../../../components/SWLoadingButtonOutlined';
import { listRmProperties, listRmUnits, listRmChargeTypes } from './pmsApi';
import type { RmProperty, RmUnit, RmChargeType } from './pmsApi';

interface RmMatchingDialogProps {
  open: boolean;
  onClose: () => void;
  units: Unit[];
  onUpdate: (pmsType: PmsType | null, pmsConfig: RentManagerConfig | null) => Promise<void>;
  onRefresh?: () => void;
  existingConfig?: RentManagerConfig | null;
  requireChargeType?: boolean;
  onSaveSuccess?: () => void;
}

export const RmMatchingDialog = ({
  open,
  onClose,
  units,
  onUpdate,
  onRefresh,
  existingConfig,
  requireChargeType,
  onSaveSuccess,
}: RmMatchingDialogProps): JSX.Element => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Company Code (Step 0) state
  const [companyCode, setCompanyCode] = React.useState<string | null>(null);
  const [companyCodeInput, setCompanyCodeInput] = React.useState('');
  const [loadingCompanyCode, setLoadingCompanyCode] = React.useState(false);
  const [savingCompanyCode, setSavingCompanyCode] = React.useState(false);
  const [editingCompanyCode, setEditingCompanyCode] = React.useState(false);
  const [accountId, setAccountId] = React.useState<string | null>(null);

  // Property selection state
  const [properties, setProperties] = React.useState<RmProperty[]>([]);
  const [loadingProperties, setLoadingProperties] = React.useState(false);
  const [selectedProperty, setSelectedProperty] = React.useState<RmProperty | null>(null);

  const [rmUnits, setRmUnits] = React.useState<RmUnit[]>([]);
  const [loadingUnits, setLoadingUnits] = React.useState(false);

  // Map of SimpleSub unit ID -> selected RmUnit
  const [unitMatches, setUnitMatches] = React.useState<Record<string, RmUnit | null>>({});

  const [chargeTypes, setChargeTypes] = React.useState<RmChargeType[]>([]);
  const [loadingChargeTypes, setLoadingChargeTypes] = React.useState(false);
  const [selectedChargeType, setSelectedChargeType] = React.useState<RmChargeType | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);

  const companyCodeReady = companyCode != null && companyCode.length > 0;

  const billableUnits = React.useMemo(
    () =>
      units
        .filter((u) => {
          const flags = (u.unit_flags ?? []) as string[];
          return !flags.includes('COMMON_AREA') && !flags.includes('NON_BILLABLE');
        })
        .sort((a, b) => naturalCompare(a.name, b.name)),
    [units],
  );

  // Set of already-selected RM unit IDs (for filtering dropdowns)
  const selectedRmUnitIds = React.useMemo(() => {
    const ids = new Set<number>();
    Object.values(unitMatches).forEach((rm) => {
      if (rm) ids.add(rm.UnitID);
    });
    return ids;
  }, [unitMatches]);

  // Step 0: Fetch account's company_code on open
  React.useEffect(() => {
    if (!open) return;
    setLoadingCompanyCode(true);
    listAccounts()
      .then((accounts) => {
        if (accounts && accounts.length > 0) {
          const account = accounts[0];
          setAccountId(account.id);
          const existingCompanyCode = account.pms_config?.company_code ?? null;
          setCompanyCode(existingCompanyCode);
          setCompanyCodeInput(existingCompanyCode ?? '');
          setEditingCompanyCode(false);
        }
      })
      .catch((err) => {
        toast.error(`Failed to load account info: ${err.message}`, { closeButton: true });
      })
      .finally(() => setLoadingCompanyCode(false));
  }, [open]);

  // Load properties and charge types once company_code is set (re-fetch when company_code changes)
  React.useEffect(() => {
    if (!open || companyCode == null || companyCode.length === 0) return;

    // Reset downstream state when company_code changes
    setSelectedProperty(null);
    setRmUnits([]);
    setUnitMatches({});
    setSelectedChargeType(null);

    setLoadingProperties(true);
    listRmProperties()
      .then((data) => {
        setProperties(data);
        // Pre-select existing property
        if (existingConfig?.property_id) {
          const match = data.find((p) => p.PropertyID === existingConfig.property_id);
          if (match) setSelectedProperty(match);
        }
      })
      .catch((err) => {
        setProperties([]);
        toast.error('Failed to load properties. Please check that your company code is correct.', {
          closeButton: true,
        });
      })
      .finally(() => setLoadingProperties(false));

    setLoadingChargeTypes(true);
    listRmChargeTypes()
      .then((data) => {
        setChargeTypes(data);
        // Pre-select existing charge type
        if (existingConfig?.charge_type_id) {
          const match = data.find((ct) => ct.ChargeTypeID === existingConfig.charge_type_id);
          if (match) setSelectedChargeType(match);
        }
      })
      .catch((err) => {
        setChargeTypes([]);
        toast.error(
          'Failed to load charge types. Please check that your company code is correct.',
          { closeButton: true },
        );
      })
      .finally(() => setLoadingChargeTypes(false));
  }, [open, companyCode, existingConfig]);

  // Reset state when dialog closes
  React.useEffect(() => {
    if (!open) {
      setSelectedProperty(null);
      setRmUnits([]);
      setUnitMatches({});
      setSelectedChargeType(null);
      setCompanyCode(null);
      setCompanyCodeInput('');
      setEditingCompanyCode(false);
      setAccountId(null);
    }
  }, [open]);

  // Load units when property changes, pre-populate matches from existing unit pms_config
  React.useEffect(() => {
    if (!selectedProperty) {
      setRmUnits([]);
      setUnitMatches({});
      return;
    }
    setLoadingUnits(true);
    listRmUnits(selectedProperty.PropertyID)
      .then((data) => {
        setRmUnits(data);
        // Pre-populate unit matches from existing pms_config.unit_id
        const matches: Record<string, RmUnit | null> = {};
        for (const unit of billableUnits) {
          if (unit.pms_config?.unit_id != null) {
            const rmMatch = data.find((rm) => rm.UnitID === unit.pms_config!.unit_id);
            if (rmMatch) {
              matches[unit.id] = rmMatch;
            }
          }
        }
        setUnitMatches(matches);
      })
      .catch((err) => {
        toast.error(`Failed to load units: ${err.message}`, { closeButton: true });
      })
      .finally(() => setLoadingUnits(false));
  }, [selectedProperty, billableUnits]);

  const handleSaveCompanyCode = async () => {
    if (!accountId) return;
    setSavingCompanyCode(true);
    try {
      const trimmed = companyCodeInput.trim();
      if (trimmed) {
        await updateAccount(accountId, {
          pms_type: 'RENT_MANAGER',
          pms_config: { company_code: trimmed },
        });
        setCompanyCode(trimmed);
      } else {
        await updateAccount(accountId, {
          pms_type: null,
          pms_config: null,
        });
        setCompanyCode(null);
      }
      setEditingCompanyCode(false);
      toast.success(trimmed ? 'Company code saved' : 'Company code cleared', { closeButton: true });
    } catch (err: any) {
      toast.error(`Failed to save company code: ${err.message}`, { closeButton: true });
    } finally {
      setSavingCompanyCode(false);
    }
  };

  const handleAutoMatch = () => {
    const matches: Record<string, RmUnit | null> = {};
    let matchCount = 0;
    const usedRmIds = new Set<number>();

    for (const unit of billableUnits) {
      const match = rmUnits.find(
        (rm) => rm.Name.toLowerCase() === unit.name.toLowerCase() && !usedRmIds.has(rm.UnitID),
      );
      if (match) {
        matches[unit.id] = match;
        usedRmIds.add(match.UnitID);
        matchCount++;
      }
    }
    setUnitMatches(matches);
    toast.info(`Auto-matched ${matchCount} of ${billableUnits.length} units`, {
      closeButton: true,
    });
  };

  const handleSave = async () => {
    if (!selectedProperty) return;

    setIsSaving(true);
    try {
      // Save structure-level config
      const config: RentManagerConfig = {
        property_short_name: selectedProperty.ShortName || selectedProperty.Name,
        property_id: selectedProperty.PropertyID,
        default_charge_type: selectedChargeType?.Name || undefined,
        charge_type_id: selectedChargeType?.ChargeTypeID || undefined,
      };
      await onUpdate('RENT_MANAGER' as PmsType, config);

      // Save unit-level PMS config (matched units get config, unmatched units get cleared)
      const savePromises: Promise<void>[] = [];
      for (const unit of billableUnits) {
        const rmUnit = unitMatches[unit.id];
        if (rmUnit) {
          savePromises.push(
            updateUnitPmsConfig(unit.id, 'RENT_MANAGER', {
              unit_name: rmUnit.Name,
              unit_id: rmUnit.UnitID,
            }),
          );
        } else if (unit.pms_config) {
          savePromises.push(updateUnitPmsConfig(unit.id, null, null));
        }
      }
      if (savePromises.length > 0) {
        await Promise.all(savePromises);
      }

      onRefresh?.();
      toast.success('PMS settings saved successfully', { closeButton: true });
      onClose();
      onSaveSuccess?.();
    } catch {
      toast.error('Failed to save PMS settings', { closeButton: true });
    } finally {
      setIsSaving(false);
    }
  };

  const matchedCount = billableUnits.filter((u) => unitMatches[u.id]).length;

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth="md">
      <DialogTitle>
        Match Units via Rent Manager API
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={isSaving}
          size="small"
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Step 0: Company Code */}
        <Box sx={{ mb: 3, mt: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Company Code
          </Typography>
          {loadingCompanyCode ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="textSecondary">
                Loading account settings...
              </Typography>
            </Box>
          ) : companyCodeReady && !editingCompanyCode ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                <strong>{companyCode}</strong>.rmx.rentmanager.com
              </Typography>
              <Link
                component="button"
                variant="body2"
                onClick={() => {
                  setCompanyCodeInput(companyCode ?? '');
                  setEditingCompanyCode(true);
                }}
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </Link>
            </Box>
          ) : (
            <>
              <TextField
                fullWidth
                size="small"
                value={companyCodeInput}
                onChange={(e) => setCompanyCodeInput(e.target.value)}
                placeholder="mycompany"
                helperText={
                  <>
                    The company code from your Rent Manager portal URL (e.g., https://
                    <strong>mycompany</strong>.rmx.rentmanager.com)
                  </>
                }
                disabled={savingCompanyCode}
                sx={{ mb: 1 }}
              />
              <SWLoadingButton
                size="small"
                onClick={handleSaveCompanyCode}
                loading={savingCompanyCode}
              >
                {editingCompanyCode
                  ? companyCodeInput.trim()
                    ? 'Update Company Code'
                    : 'Clear Company Code'
                  : 'Save Company Code'}
              </SWLoadingButton>
            </>
          )}
        </Box>

        {/* Steps below only show once company code is set */}
        {companyCodeReady && (
          <>
            {/* Step 1: Select Property */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Select Property
              </Typography>
              <Autocomplete
                options={properties}
                getOptionLabel={(opt) => `${opt.Name} (${opt.ShortName})`}
                loading={loadingProperties}
                value={selectedProperty}
                onChange={(_e, val) => setSelectedProperty(val)}
                renderInput={(params) => (
                  <TextField {...params} size="small" placeholder="Search properties..." />
                )}
              />
            </Box>

            {/* Step 2: Match Units */}
            {selectedProperty && (
              <>
                {loadingUnits ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                        Unit Mappings ({matchedCount} of {billableUnits.length} matched)
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleAutoMatch}
                        disabled={rmUnits.length === 0}
                      >
                        Auto-Match
                      </Button>
                    </Box>

                    <TableContainer sx={{ maxHeight: 350, mb: 3 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>SimpleSub Unit</TableCell>
                            <TableCell>Rent Manager Unit</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {billableUnits.map((unit) => {
                            const currentMatch = unitMatches[unit.id] ?? null;
                            // Show all RM units not selected elsewhere, plus the current selection
                            const availableOptions = rmUnits.filter(
                              (rm) =>
                                !selectedRmUnitIds.has(rm.UnitID) ||
                                rm.UnitID === currentMatch?.UnitID,
                            );
                            return (
                              <TableRow key={unit.id}>
                                <TableCell>
                                  <Typography variant="body2">{unit.name}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Autocomplete
                                    size="small"
                                    options={availableOptions}
                                    getOptionLabel={(opt) => opt.Name}
                                    value={currentMatch}
                                    onChange={(_e, val) =>
                                      setUnitMatches((prev) => ({ ...prev, [unit.id]: val }))
                                    }
                                    renderInput={(params) => (
                                      <TextField {...params} placeholder="Select RM unit..." />
                                    )}
                                    isOptionEqualToValue={(opt, val) => opt.UnitID === val.UnitID}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Charge Type */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        {requireChargeType
                          ? 'Default Charge Type (required)'
                          : 'Default Charge Type (optional)'}
                      </Typography>
                      <Autocomplete
                        options={[...chargeTypes].sort((a, b) => naturalCompare(a.Name, b.Name))}
                        getOptionLabel={(opt) => opt.Name}
                        loading={loadingChargeTypes}
                        value={selectedChargeType}
                        onChange={(_e, val) => setSelectedChargeType(val)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Select charge type..."
                            helperText={
                              requireChargeType
                                ? 'A charge type is required for uploading to Rent Manager'
                                : 'Optional charge type for Rent Manager'
                            }
                          />
                        )}
                        isOptionEqualToValue={(opt, val) => opt.ChargeTypeID === val.ChargeTypeID}
                      />
                    </Box>
                  </>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <SWLoadingButtonOutlined onClick={onClose} disabled={isSaving}>
          Cancel
        </SWLoadingButtonOutlined>
        <SWLoadingButton
          onClick={handleSave}
          loading={isSaving}
          disabled={
            !companyCodeReady ||
            !selectedProperty ||
            matchedCount === 0 ||
            (requireChargeType && !selectedChargeType)
          }
        >
          Save Mappings
        </SWLoadingButton>
      </DialogActions>
    </Dialog>
  );
};
